import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Flex
} from '@chakra-ui/react';
import {
  StarIcon,
  CalendarIcon,
  ViewIcon,
  SettingsIcon,
  TimeIcon,
  HamburgerIcon,
  InfoIcon
} from '@chakra-ui/icons';

// Define brand colors - using the same as in SideMenu
const brandColors = {
  primary: '#2c58dc',
  secondary: '#3423ba',
  accent: '#6694ec',
  light: '#edf3fa',
  dark: '#3b3dcd'
};

// Define the menu item interface
interface MenuItem {
  icon: React.ElementType;
  label: string;
  path: string;
}

export default function ResponsiveNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Using the same menu items as in SideMenu, adding Dashboard
  const menuItems: MenuItem[] = [
    { icon: InfoIcon, label: 'Dashboard', path: '/' },
    { icon: StarIcon, label: 'Teams', path: '/team' },
    { icon: CalendarIcon, label: 'Races', path: '/races' },
    { icon: ViewIcon, label: 'Racers', path: '/racers' },
    { icon: TimeIcon, label: 'Statistics', path: '/statistics' },
    // Uncomment if needed
    // { icon: SettingsIcon, label: 'Settings', path: '/settings' },
  ];

  // Function to handle navigation
  const handleNavigation = (path: string): void => {
    navigate(path);
    if (isOpen) onClose(); // Close drawer if open (mobile)
  };

  // Render menu item with consistent styling for both desktop and mobile
  const renderMenuItem = (item: MenuItem, isMobile: boolean = false) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <Box
        key={item.path}
        onClick={() => handleNavigation(item.path)}
        cursor="pointer"
        p={2}
        borderRadius="md"
        bg={isActive ? brandColors.accent : 'transparent'}
        color={isActive ? 'white' : brandColors.dark}
        _hover={{ bg: brandColors.primary, color: 'white' }}
        w={isMobile ? "full" : "auto"}
        textAlign={isMobile ? "left" : "center"}
      >
        {isMobile ? (
          <HStack spacing={3}>
            <Icon boxSize={5} />
            <Text fontWeight={isActive ? 'bold' : 'normal'}>{item.label}</Text>
          </HStack>
        ) : (
          <VStack spacing={1} align="center">
            <Icon boxSize={6} />
            <Text fontSize="xs" fontWeight={isActive ? 'bold' : 'normal'}>{item.label}</Text>
          </VStack>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Desktop Side Navigation - completely hidden on mobile */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="fixed"
        left={0}
        p={5}
        w="70px"
        top={0}
        h="100vh"
        bg={brandColors.light}
        borderRight="1px"
        borderColor={brandColors.accent}
        shadow="sm"
        zIndex={10}
      >
        <VStack spacing={6} align="center" mt={6}>
          {menuItems.map(item => renderMenuItem(item))}
        </VStack>
      </Box>

      {/* Mobile Navigation - bigger hamburger icon in the top left corner */}
      <Box 
        display={{ base: 'block', md: 'none' }} 
        position="fixed"
        top={3}
        left={3}
        zIndex={20}
      >
        <IconButton
          aria-label="Open menu"
          icon={<HamburgerIcon boxSize={6} />}
          onClick={onOpen}
          size="md"
          variant="ghost"
          color={brandColors.dark}
          bg="transparent"
          _hover={{ bg: 'rgba(0,0,0,0.05)' }}
        />
      </Box>
      
      {/* Mobile Drawer Menu */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={brandColors.light}>
          <DrawerCloseButton color={brandColors.dark} />
          <DrawerHeader borderBottomWidth="1px" color={brandColors.dark}>Menu</DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch" mt={4}>
              {menuItems.map(item => renderMenuItem(item, true))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

    </>
  );
} 